import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartActionButtonSx,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';

const apiUrl = process.env.REACT_APP_API_URL;
const emptyForm = { breedArabic: '', breedEnglish: '' };

const HorseBreedList = () => {
  const { t, i18n } = useTranslation();
  const [breeds, setBreeds] = useState([]);
  const [editBreedId, setEditBreedId] = useState(null);
  const [deleteBreedId, setDeleteBreedId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const isRtl = String(i18n.language || '').startsWith('ar');

  const fetchBreeds = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${apiUrl}/horse_breeds`);
      setBreeds(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || t('horse_breeds_load_error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchBreeds();
  }, [fetchBreeds]);

  const filteredBreeds = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return breeds;
    return breeds.filter((breed) => (
      String(breed.breed_arabic || '').toLowerCase().includes(term)
      || String(breed.breed_english || '').toLowerCase().includes(term)
    ));
  }, [breeds, search]);
  const {
    page,
    rowsPerPage,
    paginatedItems: paginatedBreeds,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(filteredBreeds, [search]);

  const resetForm = useCallback(() => {
    setEditBreedId(null);
    setIsAddDialogOpen(false);
    setForm(emptyForm);
  }, []);

  const openAddDialog = useCallback(() => {
    setMessage('');
    setError('');
    setForm(emptyForm);
    setIsAddDialogOpen(true);
  }, []);

  const openEditDialog = useCallback((breed) => {
    setMessage('');
    setError('');
    setEditBreedId(breed.breed_id);
    setForm({
      breedArabic: breed.breed_arabic || '',
      breedEnglish: breed.breed_english || '',
    });
  }, []);

  const validateForm = useCallback(() => {
    if (!form.breedArabic.trim() || !form.breedEnglish.trim()) {
      setError(t('horse_breed_fields_required'));
      return false;
    }
    return true;
  }, [form.breedArabic, form.breedEnglish, t]);

  const saveBreed = useCallback(async () => {
    if (!validateForm()) return;
    setSaving(true);
    setError('');
    setMessage('');
    const payload = {
      breed_arabic: form.breedArabic.trim(),
      breed_english: form.breedEnglish.trim(),
    };
    try {
      if (editBreedId) {
        await axios.put(`${apiUrl}/horse_breeds/${editBreedId}`, payload);
        setMessage(t('horse_breed_updated_successfully'));
      } else {
        await axios.post(`${apiUrl}/horse_breeds`, payload);
        setMessage(t('horse_breed_added_successfully'));
      }
      resetForm();
      await fetchBreeds();
    } catch (err) {
      setError(err?.response?.data?.error || (editBreedId ? t('horse_breed_update_error') : t('horse_breed_add_error')));
    } finally {
      setSaving(false);
    }
  }, [editBreedId, fetchBreeds, form.breedArabic, form.breedEnglish, resetForm, t, validateForm]);

  const handleDelete = useCallback(async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await axios.delete(`${apiUrl}/horse_breeds/${deleteBreedId}`);
      setDeleteBreedId(null);
      setMessage(t('horse_breed_deleted_successfully'));
      await fetchBreeds();
    } catch (err) {
      setError(err?.response?.data?.error || t('horse_breed_delete_error'));
    } finally {
      setSaving(false);
    }
  }, [deleteBreedId, fetchBreeds, t]);

  return (
    <Box
      className="smart-page-container"
      sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <SettingsPageHeader
        title={t('horse_breeds')}
        subtitle={t('horse_breeds_hint')}
        count={breeds.length}
        actions={(
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
            sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}
          >
            {t('add_breed')}
          </Button>
        )}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            label={t('search_breeds')}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: COLORS.muted, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} /> }}
            sx={{ maxWidth: { sm: 360 } }}
            fullWidth
          />
        </Stack>
        <TableContainer sx={smartTableContainerSx}>
          <Table size="small" sx={smartTableSx}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell align="center" sx={{ width: 72 }}>#</TableCell>
                <TableCell>{t('breed_arabic')}</TableCell>
                <TableCell>{t('breed_english')}</TableCell>
                <TableCell align="center" sx={{ width: 150 }}>{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedBreeds.map((breed, index) => (
                <TableRow key={breed.breed_id} hover>
                  <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 900, minWidth: 180 }}>{breed.breed_arabic}</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>{breed.breed_english}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('edit_breed')}>
                        <IconButton onClick={() => openEditDialog(breed)} sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete_breed')}>
                        <IconButton onClick={() => setDeleteBreedId(breed.breed_id)} sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredBreeds.length && !loading && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>
                      {search ? t('no_search_results') : t('no_breeds_found')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <SmartTablePagination
          count={filteredBreeds.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog open={Boolean(editBreedId) || isAddDialogOpen} onClose={resetForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{editBreedId ? t('edit_breed') : t('add_breed')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField fullWidth label={t('breed_arabic')} value={form.breedArabic} onChange={(event) => setForm((current) => ({ ...current, breedArabic: event.target.value }))} />
            <TextField fullWidth label={t('breed_english')} value={form.breedEnglish} onChange={(event) => setForm((current) => ({ ...current, breedEnglish: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={resetForm} sx={{ color: COLORS.muted, fontWeight: 900 }}>
            {t('cancel')}
          </Button>
          <Button onClick={saveBreed} variant="contained" disabled={saving} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteBreedId)} onClose={() => setDeleteBreedId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('delete_confirmation')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('delete_breed_confirmation')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteBreedId(null)} sx={{ color: COLORS.muted, fontWeight: 900 }}>
            {t('cancel')}
          </Button>
          <Button onClick={handleDelete} disabled={saving} sx={{ color: COLORS.rose, fontWeight: 900 }}>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HorseBreedList;
